import { isEmpty, keyBy } from 'lodash/fp';
// Utils
import * as rectLib from '../../../../../common/maths/geometry/rect';
import * as pointLib from '../../../../../common/maths/geometry/point';
import { getPathBoundingRect, getPathGroups } from '../../drawingEditor/drawingEditorUtils';
import { getDrawingElementContent } from '../../drawingEditor/drawingEditorElementContentUtils';

import { BoardSections } from '../../../../../common/boards/boardConstants';

export const getCanvasViewportTranslation = ({ drawEditorDomNode, canvasViewportDomNode }) => {
    const drawEditorRect = drawEditorDomNode?.getBoundingClientRect();
    const canvasViewportClientRect = canvasViewportDomNode?.getBoundingClientRect();

    return drawEditorRect && canvasViewportClientRect
        ? pointLib.difference(canvasViewportClientRect, drawEditorRect)
        : { x: 0, y: 0 };
};

const EMPTY_RECT = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0,
};

const getDrawingGridAlignedBoundingRect = (paths, gridSize) => {
    if (!paths || !paths.length) return EMPTY_RECT;

    const strokeBoundingRects = paths.map(getPathBoundingRect);
    const drawingBoundingRect = rectLib.getBoundingRect(strokeBoundingRects);

    // we need to calculate this such that the bounding rectangle
    // extends outward in each direction to the next grid cell.
    // rounding won't quite do it, as eg -0.1 will round to 0
    const left = Math.floor(drawingBoundingRect.left / gridSize) * gridSize;
    const right = Math.ceil(drawingBoundingRect.right / gridSize) * gridSize;
    const top = Math.floor(drawingBoundingRect.top / gridSize) * gridSize;
    const bottom = Math.ceil(drawingBoundingRect.bottom / gridSize) * gridSize;

    const width = right - left;
    const height = bottom - top;

    return { top, right, bottom, left, width, height };
};

export const createAnnotationElementDefinition = ({
    paths,
    currentBoardId,
    gridSize,
    canvasTranslation,
    canvasOrigin,
    positionScore,
}) => {
    // Filter out any paths that don't have a bounding rect - this can happen when the
    //  perfect-freehand library returns "NaNs" for a line, such as when doing a single click
    const filteredPaths = paths.filter((path) => {
        const pathBoundingRect = getPathBoundingRect(path);
        return pathBoundingRect && pathBoundingRect.width > 0;
    });

    if (isEmpty(filteredPaths)) return;

    const boundingRect = getDrawingGridAlignedBoundingRect(filteredPaths, gridSize);

    if (boundingRect.width <= 0) return;

    const drawing = getDrawingElementContent({ paths: filteredPaths, boundingRect });

    const translatedBoundingRect = rectLib.translate(canvasTranslation, boundingRect);

    // translatedBoundingRect's coordinates are guaranteed to be
    // divisible by gridSize, so we don't need to round here
    const gridUnits = {
        x: translatedBoundingRect.left / gridSize,
        y: translatedBoundingRect.top / gridSize,
    };

    const position = pointLib.translate(gridUnits, canvasOrigin);
    position.score = positionScore;

    return {
        location: {
            section: BoardSections.CANVAS,
            parentId: currentBoardId,
            position,
        },
        content: {
            width: (boundingRect.width + 2) / gridSize,
            drawing,
        },
    };
};

/**
 * Groups paths that are close into individual element definitions.
 */
export const createGroupedAnnotationElementDefinitions = ({
    paths,
    currentBoardId,
    gridSize,
    canvasTranslation,
    canvasOrigin,
}) => {
    const pathsMap = keyBy('id', paths);
    const pathGroups = getPathGroups({ paths, gridSize });

    return pathGroups
        .map((groupPathIds) => {
            const groupPaths = groupPathIds.map((pathId) => pathsMap[pathId]);

            return createAnnotationElementDefinition({
                paths: groupPaths,
                currentBoardId,
                gridSize,
                canvasTranslation,
                canvasOrigin,
            });
        })
        .filter((def) => !isEmpty(def));
};
